<template>
  <div class="app-container">
    <!--加载进度-->
    <van-loading v-if="!isLoaded" class="loading" />
    <!--加载失败-->
    <div v-else-if="errorMessage" class="error-message">
      错误：{{ errorMessage }}
    </div>
    <!--加载成功-->
    <template v-else>
      <van-steps :active="step">
        <van-step>选择业务类型</van-step>
        <van-step>填写申请信息</van-step>
        <van-step>上传附件材料</van-step>
      </van-steps>
      <!--业务-->
      <business-type-tree-select
        v-show="step===0"
        :business-type-id="applicationInfo.businessTypeId"
        @change="handleBusinessTypeChange"
      />
      <!--表单-->
      <application-form
        v-show="step===1"
        ref="applicationForm"
        :application-info="applicationInfo"
        action="Start"
        :handle-submit="startApplication"
        @validate="handleApplicationFormValidate"
      />
      <!--附件-->
      <attachment-list
        v-show="step===2"
        :case-attach-directories="applicationInfo.caseInfo.caseAttachDirectories"
        @change="handleAttachmentListChange"
        @validate="handleAttachmentListValidate"
      />
      <!--按钮-->
      <div class="button-wrap">
        <van-row gutter="15">
          <van-col v-if="!isBackDisabled" :span="12">
            <van-button type="primary" size="large" text="上一步" plain @click="handleBack" />
          </van-col>
          <van-col :span="submitSpan">
            <van-button
              type="primary"
              size="large"
              :text="submitText"
              :disabled="isSubmitDisabled"
              @click="handleSubmit"
            />
          </van-col>
        </van-row>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BusinessTypeTreeSelect,
  ApplicationForm,
  AttachmentList
} from './components'
import {
  startApplicationInfo,
  submitApplicationInfo
} from '@/api/application'
import { Toast } from 'vant'
import to from 'await-to-js'

export default {
  components: {
    BusinessTypeTreeSelect,
    ApplicationForm,
    AttachmentList
  },
  data() {
    return {
      isLoaded: false,
      errorMessage: '',
      step: 0,
      applicationInfo: {
        businessTypeId: '',
        businessTypeName: '',
        businessTypeAlias: '',
        businessTypeCode: '',
        aTBusinessTypeCode: '',
        registrationItemId: '',
        registrationItemName: '',
        registrationItemAlias: '',
        registrationItemCode: '',
        applicantName: '',
        applicantIdNumber: '',
        applicantMobilePhone: '',
        remark: '',
        caseInfo: {
          caseAttachDirectories: []
        },
        jJQLRs: [],
        qlrList: [],
        ywrList: [],
        bDCQZH: '', // 产权证号
        bDCDYH: '', // 不动产单元号
        sQZT: '', // 申请主体
        bHNR: '' // 变化内容
      },
      startedRegistrationItemId: null,
      isApplicationFormValid: false,
      isAttachmentListValid: false,
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters(['webUserInfo']),
    caseAttachDirectories() {
      return this.applicationInfo.caseInfo.caseAttachDirectories
    },
    submitText() {
      switch (this.step) {
        case 0: // 事项
          return '下一步'
        case 1: // 表单
          return '下一步'
        case 2: // 附件
          return '提交'
        default:
          return '提交'
      }
    },
    isSubmitDisabled() {
      switch (this.step) {
        case 0: // 事项
          return !this.applicationInfo.businessTypeId
        case 1: // 表单
          return !this.isApplicationFormValid || this.isSubmitting
        case 2: // 附件
          return !this.isAttachmentListValid || this.isSubmitting
        default:
          return false
      }
    },
    isBackDisabled() {
      return this.step === 0
    },
    submitSpan() {
      return this.isBackDisabled ? 24 : 12
    }
  },
  watch: {
    step: function() {
      if (this.step === 0) {
        // 允许浏览器回退
        this.$store.dispatch('app/setBackInfo', { allowBack: true })
      } else {
        // 禁止浏览器回退，切换到上一步
        this.$store.dispatch('app/setBackInfo', {
          allowBack: false,
          backHandler: this.handleBack
        })
      }
    }
  },
  created() {
    // 设置申请信息
    this.setApplicationInfo()
  },
  methods: {
    // 设置申请信息
    async setApplicationInfo() {
      // 默认传入的业务类型id
      const businessTypeId = this.$route.query.businessTypeId
      // 默认传入的登记事项id
      const registrationItemId = this.$route.query.registrationItemId
      // 设置默认申请信息
      if (businessTypeId || registrationItemId) {
        const [error, businessTypes] = await to(
          this.$store.dispatch('application/getBusinessTypes')
        )
        if (error) {
          // 设置错误信息
          this.errorMessage = error.message
        } else {
          // 设置默认申请信息
          if (registrationItemId) {
            // 根据登记事项id设置业务类型和登记事项信息
            const businessType = businessTypes.find(t =>
              t.registrationItems.some(i => i.id === registrationItemId)
            )
            if (businessType) {
              Object.assign(this.applicationInfo, {
                businessTypeId: businessType.id,
                businessTypeName: businessType.name,
                businessTypeAlias: businessType.alias,
                businessTypeCode: businessType.code,
                aTBusinessTypeCode: businessType.aTBusinessTypeCode
              })
              const registrationItem = businessType.registrationItems.find(
                i => i.id === registrationItemId
              )
              if (registrationItem) {
                Object.assign(this.applicationInfo, {
                  registrationItemId: registrationItem.id,
                  registrationItemName: registrationItem.name,
                  registrationItemAlias: registrationItem.alias,
                  registrationItemCode: registrationItem.code
                })
                // 切换至填写表单环节
                this.step = 1
              }
            }
          } else if (businessTypeId) {
            // 根据业务类型id设置业务类型信息
            const businessType = businessTypes.find(
              t => t.id === businessTypeId
            )
            if (businessType) {
              Object.assign(this.applicationInfo, {
                businessTypeId: businessType.id,
                businessTypeName: businessType.name,
                businessTypeAlias: businessType.alias,
                businessTypeCode: businessType.code
              })
            }
          }
        }
      }
      // 设置申请人信息
      this.setApplicantInfo()
      // 设置加载状态为已加载
      this.isLoaded = true
    },
    // 设置申请人信息
    setApplicantInfo() {
      Object.assign(this.applicationInfo, {
        applicantId: this.webUserInfo.id,
        applicantName: this.webUserInfo.realName,
        applicantIdNumber: this.webUserInfo.idNumber,
        applicantMobilePhone: this.webUserInfo.mobilePhone
      })
    },
    // 处理业务类型改变
    handleBusinessTypeChange(businessType) {
      console.info(businessType)
      Object.assign(this.applicationInfo, {
        businessTypeId: businessType.id,
        businessTypeName: businessType.name,
        businessTypeAlias: businessType.alias,
        businessTypeCode: businessType.code,
        aTBusinessTypeCode: businessType.aTBusinessTypeCode,
        registrationItemId: '',
        registrationItemName: '',
        registrationItemAlias: '',
        registrationItemCode: '',
        jJQLRs: []
      })
    },
    // 处理申请表单验证
    handleApplicationFormValidate(isValid) {
      this.isApplicationFormValid = isValid
    },
    // 处理附件列表验证
    handleAttachmentListValidate(isValid) {
      this.isAttachmentListValid = isValid
    },
    // 处理附件列表改变
    handleAttachmentListChange(caseAttachDirectories) {
      this.applicationInfo.caseInfo.caseAttachDirectories = caseAttachDirectories
    },
    // 处理回退
    handleBack() {
      if (this.step > 0) {
        // 把申请信息清空
        this.emptyApplicationInfo()
        this.step--
      }
    },
    // 处理提交
    handleSubmit() {
      if (this.step === 0) {
        // 事项
        this.step++
      } else if (this.step === 1) {
        // 表单
        if (
          this.applicationInfo.registrationItemId ===
          this.startedRegistrationItemId
        ) {
          // 已提交
          // do nothing
          this.step++
        } else {
          // 验证房屋状态
          this.$refs.applicationForm.getCheckHouseStatus()
        }
      } else if (this.step === 2) {
        // 附件
        this.isSubmitting = true
        Toast.loading('提交业务...')
        // 提交申请
        submitApplicationInfo({
          id: this.applicationInfo.id,
          remark: this.applicationInfo.remark
        })
          .then(response => {
            // 提交成功，重定向
            Toast.success({
              message: '提交成功',
              duration: 1000,
              onClose: () => {
                this.$router.replace({
                  name: 'ApplicationList',
                  params: { status: 'Pending' }
                })
              }
            })
          })
          .catch(error => {
            // 提示异常
            const data = error.response.data
            if (data.message) {
              Toast.fail(data.message)
            }
          })
          .finally(() => {
            this.isSubmitting = false
          })
      }
    },
    // 清空申请信息
    emptyApplicationInfo() {
      this.$refs.applicationForm.resetApplicationInfo()
    },
    startApplication() {
      // 未提交
      this.isSubmitting = true
      Toast.loading('创建业务...')
      // 发起申请
      startApplicationInfo(this.applicationInfo)
        .then(({ data }) => {
          Toast.clear()
          if (data.caseInfo != null) {
            // 案卷附件目录排序
            data.caseInfo.caseAttachDirectories = data.caseInfo.caseAttachDirectories.sort(
              (a, b) => a.orderNo - b.orderNo
            )
            // 案卷附件排序
            data.caseInfo.caseAttachDirectories.forEach(d => {
              d.caseAttaches = d.caseAttaches.sort((a, b) =>
                a.name.localeCompare(b.name)
              )
            })
          }
          Object.assign(this.applicationInfo, data)
          this.startedRegistrationItemId = data.registrationItemId
          this.step++
        })
        .catch(error => {
          // 提示异常
          const data = error.response.data
          if (data.message) {
            Toast.fail(data.message)
          }
        })
        .finally(() => {
          this.isSubmitting = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.app-container {
  min-height: 100vh;
  background-color: #ededed;
  .loading {
    padding-top: 15px;
    text-align: center;
  }
  .error-message {
    color: #ee0a24;
    padding-top: 15px;
    text-align: center;
  }
  .button-wrap {
    margin-top: 15px;
    padding: 0 15px 15px 15px;
  }
}
</style>
